import React, { useState, useEffect } from 'react';
import { X, Mail, CheckCircle, Shield, Gift } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';

const EmailVerificationModal = ({ isOpen, onClose, userEmail }) => {
  const [isResending, setIsResending] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(null);

  // Check for existing cooldown
  useEffect(() => {
    if (!isOpen) return;

    const cooldownUntil = localStorage.getItem('emailResendCooldownUntil');
    if (cooldownUntil) {
      const cooldownDate = new Date(cooldownUntil);
      if (cooldownDate > new Date()) {
        setCooldownTime(cooldownDate);
      } else {
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!cooldownTime) return;

    const interval = setInterval(() => {
      if (new Date() >= cooldownTime) {
        setCooldownTime(null);
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownTime]);

  // Listen for cooldown updates from localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'emailResendCooldownUntil' && e.newValue) {
        setCooldownTime(new Date(e.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen to custom event for same-tab updates
    const handleCooldownUpdate = (e) => {
      if (e.detail) {
        setCooldownTime(new Date(e.detail));
      }
    };
    window.addEventListener('emailCooldownUpdate', handleCooldownUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('emailCooldownUpdate', handleCooldownUpdate);
    };
  }, []);

  const handleResendEmail = async () => {
    if (cooldownTime) return;

    setIsResending(true);
    try {
      const functions = getFunctions();
      const resendEmail = httpsCallable(functions, 'apiResendEmailVerification');
      const result = await resendEmail();

      if (result.data.success) {
        toast.success('Verification email sent! Please check your inbox.');
        
        // Set 10-minute cooldown
        const cooldownUntil = new Date(Date.now() + 10 * 60 * 1000);
        localStorage.setItem('emailResendCooldownUntil', cooldownUntil.toISOString());
        setCooldownTime(cooldownUntil);
        
        // Dispatch custom event for same-tab updates
        window.dispatchEvent(new CustomEvent('emailCooldownUpdate', { detail: cooldownUntil.toISOString() }));
      } else if (result.data.alreadyVerified) {
        toast.info('Your email is already verified!');
        onClose();
      } else if (result.data.rateLimited) {
        toast.error(`Please wait ${result.data.waitMinutes} minutes before requesting another email.`);
      } else {
        toast.error(result.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const getRemainingTime = () => {
    if (!cooldownTime) return null;
    const diff = cooldownTime - new Date();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-fadeIn">
        {/* Header with gradient */}
        <div className="relative bg-gradient-to-br from-cyan-50 to-teal-100 p-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
              <Mail className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Verify Your Email
          </h2>
          <p className="text-gray-600 text-center text-sm">
            We've sent a verification email to:
          </p>
          <p className="text-cyan-700 font-semibold text-center mt-1">
            {userEmail}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Gift className="w-4 h-4 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Get 100 FREE Credits</h3>
                <p className="text-sm text-gray-600">
                  Verify your email to receive 100 free credits instantly!
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Unlock Full Access</h3>
                <p className="text-sm text-gray-600">
                  Access all features and start creating amazing designs.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Secure Your Account</h3>
                <p className="text-sm text-gray-600">
                  Keep your account safe and receive important updates.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700 text-center">
              <span className="font-semibold">Didn't receive the email?</span>
              <br />
              Check your spam folder or click below to resend.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleResendEmail}
              disabled={isResending || !!cooldownTime}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-teal-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
            >
              <Mail className="w-5 h-5" />
              {isResending ? 'Sending...' : cooldownTime ? `Wait ${getRemainingTime()}` : 'Resend Verification Email'}
            </button>
            
            <button
              onClick={onClose}
              className="w-full px-6 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationModal;
